
import { supabase } from "@/integrations/supabase/client";
import { ContentItem, ContentKind } from "@/types/course";

/**
 * Adds a new content item to a lesson
 */
export async function addContentItem(
  lesson_id: string,
  title: string,
  description: string | null,
  content_type: string,
  url: string | null,
  file_path: string | null,
  is_required: boolean,
  order_index: number,
  duration: number | null
): Promise<string> {
  const { data, error } = await supabase
    .from('course_content_items')
    .insert({
      lesson_id,
      title,
      description,
      content_type,
      url,
      file_path,
      is_required,
      order_index,
      duration
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/**
 * Updates an existing content item
 */
export async function updateContentItem(id: string, updates: {
  title?: string;
  description?: string | null;
  content_type?: string;
  url?: string | null;
  file_path?: string | null;
  is_required?: boolean;
  order_index?: number;
  duration?: number | null;
}): Promise<void> {
  const { error } = await supabase
    .from('course_content_items')
    .update(updates)
    .eq('id', id);

  if (error) throw error;
}

/**
 * Deletes a content item
 */
export async function deleteContentItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('course_content_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Gets all content items for a specific lesson
 */
export async function getContentItemsByLessonId(lessonId: string): Promise<ContentItem[]> {
  const { data, error } = await supabase
    .from('course_content_items')
    .select('*')
    .eq('lesson_id', lessonId)
    .order('order_index');

  if (error) throw error;
  
  // Transform database records to match our ContentItem interface
  return (data || []).map(item => ({
    id: item.id,
    title: item.title,
    type: (item.content_type as ContentKind) || 'reading',
    content_type: item.content_type,
    url: item.url || undefined,
    file_path: item.file_path || undefined,
    lesson_id: item.lesson_id,
    is_required: item.is_required,
    duration: item.duration || undefined,
    description: item.description || undefined,
    order_index: item.order_index,
    completed: false, // Default value since DB doesn't store this
    created_at: item.created_at,
    updated_at: item.updated_at
  }));
}

/**
 * Reorders content items within a lesson
 */
export async function reorderContentItems(lessonId: string, orderedIds: string[]): Promise<void> {
  // Create an array of updates to execute in a transaction-like manner
  const updates = orderedIds.map((id, index) => {
    return supabase
      .from('course_content_items')
      .update({ order_index: index })
      .eq('id', id)
      .eq('lesson_id', lessonId);
  });

  // Execute all updates
  await Promise.all(updates);
}
